import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { MailCheck, ExternalLink, RefreshCw, PenLine, Loader2 } from "lucide-react";

export function webmailUrl(email = "") {
  const domain = (email.split("@")[1] || "").toLowerCase();
  if (domain.includes("gmail") || domain.includes("googlemail")) return "https://mail.google.com/";
  if (["outlook.com", "hotmail.com", "live.com", "msn.com"].some((d) => domain.includes(d))) return "https://outlook.live.com/mail/";
  if (domain.includes("yahoo")) return "https://mail.yahoo.com/";
  if (domain.includes("icloud") || domain.includes("me.com")) return "https://www.icloud.com/mail";
  return null;
}

/** Shared verification actions: resend, change email, confirm verified. */
export function VerifyEmailActions({ onVerified, compact = false }) {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const email = user?.email || "";
  const mail = webmailUrl(email);

  const resend = async () => {
    setBusy("resend");
    try {
      await api.post("/auth/resend-verification");
      toast.success(`Verification email sent to ${email}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the email.");
    } finally { setBusy(""); }
  };

  const check = async () => {
    setBusy("check");
    try {
      await refresh();
      const { data } = await api.get("/auth/me");
      if (data?.user?.email_verified) {
        toast.success("Email verified. Thanks!");
        onVerified?.();
      } else {
        toast.error("We still can't see a verification. Open the link in the email, then try again.");
      }
    } catch {
      toast.error("Could not check your verification status.");
    } finally { setBusy(""); }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setBusy("email");
    try {
      const { data } = await api.post("/auth/change-email", { email: newEmail });
      await refresh();
      setEditing(false); setNewEmail("");
      toast.success(`Email changed to ${data.email}. We've sent a new verification link.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change your email address.");
    } finally { setBusy(""); }
  };

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      <div className="flex flex-wrap gap-2">
        {mail && (
          <a href={mail} target="_blank" rel="noreferrer" className="fx-btn-primary text-white text-sm px-4 py-2 rounded-lg inline-flex items-center gap-2" data-testid="verify-open-email">
            <ExternalLink size={14} /> Open email
          </a>
        )}
        <button onClick={resend} disabled={!!busy} className="border border-white/10 hover:border-white/25 text-slate-200 text-sm px-4 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-60" data-testid="verify-resend">
          {busy === "resend" ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Resend verification email
        </button>
        <button onClick={check} disabled={!!busy} className="border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-200 text-sm px-4 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-60" data-testid="verify-check">
          {busy === "check" ? <Loader2 size={14} className="animate-spin" /> : <MailCheck size={14} />} I've verified my email
        </button>
        {!editing && (
          <button onClick={() => { setEditing(true); setNewEmail(email); }} className="text-slate-400 hover:text-slate-200 text-sm px-3 py-2 rounded-lg inline-flex items-center gap-2" data-testid="verify-change-email">
            <PenLine size={14} /> Change email address
          </button>
        )}
      </div>
      {editing && (
        <form onSubmit={changeEmail} className="flex flex-wrap items-end gap-2" data-testid="verify-change-email-form">
          <label className="flex-1 min-w-[220px]">
            <div className="text-xs text-slate-400 mb-1">New email address</div>
            <input required type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="w-full bg-[#0D1224] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid="verify-new-email-input" />
          </label>
          <button disabled={!!busy} className="fx-btn-primary text-white text-sm px-4 py-2 rounded-lg disabled:opacity-60" data-testid="verify-save-email">{busy === "email" ? "Saving…" : "Save and resend"}</button>
          <button type="button" onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-200 text-sm px-3 py-2">Cancel</button>
        </form>
      )}
    </div>
  );
}
